/**
 * step-debug Component - Debug View of a Single Step
 *
 * Renders a single step in DEBUG mode showing:
 * - All tool calls with full raw data
 * - System prompts
 * - Model requests
 * - Complete response information
 *
 * Memoized to prevent re-renders when props haven't changed
 */

import React from 'react'
import { Step } from '@sourcepilot/generated/chat/api/step';
import { ChatRoleType } from '@sourcepilot/generated/shared/enums';
import { UserMessage } from '@sourcepilot/ui/framework/ui/atoms/user-message'
import { StepRequestsDebug } from './StepRequestsDebug'

interface StepDebugProps {
  step: Step;
}

export const StepDebug = React.memo(function StepDebug({ step }: StepDebugProps) {
  // Show user message and all associated model requests/responses
  if (step.role === ChatRoleType.USER) {
    return (
      <>
        <UserMessage message={step} />
        {/* Render all model requests with debug config */}
        <StepRequestsDebug step={step} />
      </>
    );
  }

  // Other step roles (SYSTEM, AGENT, etc.) can be added here as needed
  return null;
}, (prevProps, nextProps) => {
  // Custom comparison: re-render if step.id, created_at changes, or when transitioning from pending
  // Also re-render when id changes from local- to server UUID
  const prevIsPending = !prevProps.step.id || prevProps.step.id.startsWith('local-');
  const nextIsPending = !nextProps.step.id || nextProps.step.id.startsWith('local-');
  return prevProps.step.id === nextProps.step.id &&
         prevProps.step.created_at === nextProps.step.created_at &&
         prevIsPending === nextIsPending;
});

StepDebug.displayName = 'StepDebug';
